/**
 * BlinterestManagement — Moderation queue + Reports queue.
 * Data-only prop drilling from the parent Admin tab.
 */
import { Check, Flag } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { resolveAssetUrl } from "@/lib/assetUrl";
import BlinterestPinForm from "@/components/BlinterestPinForm";

export default function BlinterestManagement({
  pins,
  loading,
  statusFilter,
  onStatusFilterChange,
  sourceFilter,
  onSourceFilterChange,
  onBulkPublish,
  onPublish,
  onRemove,
  onUpdateCta,
  reports,
  reportsLoading,
  reportsFilter,
  onReportsFilterChange,
  onDismissReport,
  onResolveReport,
}) {
  return (
    <>
      {/* Moderation queue */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 flex-wrap gap-3">
          <CardTitle className="font-heading text-lg text-tbc-navy">
            Moderation Queue ({pins.length})
          </CardTitle>
          <div className="flex gap-2 items-center">
            <select
              value={statusFilter}
              onChange={(e) => onStatusFilterChange(e.target.value)}
              className="h-9 border rounded-md px-2 text-sm"
              data-testid="status-filter"
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="removed">Removed</option>
              <option value="">All</option>
            </select>
            <select
              value={sourceFilter}
              onChange={(e) => onSourceFilterChange(e.target.value)}
              className="h-9 border rounded-md px-2 text-sm"
              data-testid="source-filter"
            >
              <option value="">All sources</option>
              <option value="business">Business</option>
              <option value="admin">Admin curated</option>
              <option value="tbc_curated_scrape">Scraped</option>
            </select>
            {statusFilter === "draft" && pins.length > 0 && (
              <Button
                onClick={onBulkPublish}
                size="sm"
                className="bg-tbc-gold text-tbc-navy rounded-full"
                data-testid="bulk-publish-btn"
              >
                <Check className="w-3 h-3 mr-1" />
                Publish all
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={`skel-pin-${i}`} className="aspect-square bg-gray-100 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : pins.length === 0 ? (
            <p className="text-center text-gray-500 py-8 text-sm">Queue is empty.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {pins.map((p) => (
                <BlinterestPinForm
                  key={p.pin_id}
                  pin={p}
                  onPublish={onPublish}
                  onRemove={onRemove}
                  onUpdateCta={onUpdateCta}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reports queue */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 flex-wrap gap-3">
          <CardTitle className="font-heading text-lg text-tbc-navy flex items-center gap-2">
            <Flag className="w-4 h-4 text-red-500" />
            Reports Queue ({reports.length})
          </CardTitle>
          <select
            value={reportsFilter}
            onChange={(e) => onReportsFilterChange(e.target.value)}
            className="h-9 border rounded-md px-2 text-sm"
            data-testid="reports-filter"
          >
            <option value="pending">Pending</option>
            <option value="resolved">Resolved</option>
            <option value="dismissed">Dismissed</option>
            <option value="">All</option>
          </select>
        </CardHeader>
        <CardContent>
          {reportsLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={`skel-report-${i}`} className="h-16 bg-gray-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : reports.length === 0 ? (
            <p className="text-center text-gray-500 py-6 text-sm">
              No {reportsFilter || "any"} reports right now.
            </p>
          ) : (
            <div className="space-y-3">
              {reports.map((r) => (
                <div
                  key={r.report_id}
                  className="flex flex-wrap items-start gap-3 border border-gray-200 rounded-xl p-3"
                  data-testid={`report-row-${r.report_id}`}
                >
                  {r.pin?.image_url && (
                    <Link
                      to={`/blinterest/pin/${r.pin.pin_id}`}
                      className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-none"
                    >
                      <img src={resolveAssetUrl(r.pin.image_url)} alt="" className="w-full h-full object-cover" />
                    </Link>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-body text-tbc-navy line-clamp-1">
                      {r.pin?.title || "(pin missing)"}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      <span className="uppercase tracking-wide text-red-600 font-semibold">{r.reason}</span>
                      {" · reported by "}
                      {r.reporter?.name || r.reporter?.email || r.reporter_user_id || "unknown"}
                    </p>
                    {r.note && <p className="text-xs text-gray-600 mt-1 italic">&ldquo;{r.note}&rdquo;</p>}
                  </div>
                  {r.status === "pending" && (
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onDismissReport(r.report_id)}
                        className="h-8 text-xs"
                        data-testid={`dismiss-${r.report_id}`}
                      >
                        Dismiss
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => onResolveReport(r.report_id, false)}
                        className="h-8 text-xs bg-tbc-navy text-white"
                        data-testid={`resolve-keep-${r.report_id}`}
                      >
                        Keep pin
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => onResolveReport(r.report_id, true)}
                        className="h-8 text-xs bg-red-500 hover:bg-red-600 text-white"
                        data-testid={`resolve-remove-${r.report_id}`}
                      >
                        Remove pin
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
